import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Globe } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

export const LanguageMenu = ({ mobile = false }) => {
    const { lang, setLang, langs } = useLanguage();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    if (mobile) {
        return (
            <div className="mt-3 flex flex-wrap gap-2 px-3 pb-2" data-testid="language-menu-mobile">
                {langs.map((l) => (
                    <button
                        key={l.code}
                        type="button"
                        onClick={() => setLang(l.code)}
                        data-testid={`lang-mobile-${l.code}`}
                        aria-pressed={lang === l.code}
                        className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors duration-300 ${
                            lang === l.code
                                ? "border-nx-lime/50 bg-nx-lime/10 text-nx-lime"
                                : "border-white/10 bg-nx-elev text-nx-sub hover:text-nx-text"
                        }`}
                    >
                        {l.label}
                    </button>
                ))}
            </div>
        );
    }

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                data-testid="language-menu-button"
                onClick={() => setOpen((v) => !v)}
                aria-label="Change language"
                aria-expanded={open}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-nx-elev text-nx-sub transition-colors duration-300 hover:border-white/20 hover:text-nx-text focus:outline-none focus:ring-2 focus:ring-nx-lime"
            >
                <Globe size={15} strokeWidth={1.5} aria-hidden="true" />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.97 }}
                        transition={{ duration: 0.2 }}
                        className="absolute end-0 top-11 w-44 overflow-hidden rounded-xl border border-white/10 bg-nx-elev shadow-[0_16px_48px_rgba(0,0,0,0.6)]"
                        data-testid="language-menu-panel"
                        role="menu"
                    >
                        {langs.map((l) => (
                            <button
                                key={l.code}
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setLang(l.code);
                                    setOpen(false);
                                }}
                                data-testid={`lang-option-${l.code}`}
                                className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-semibold text-nx-sub transition-colors duration-200 hover:bg-white/5 hover:text-nx-text"
                            >
                                <span>{l.label}</span>
                                {lang === l.code && <Check size={14} strokeWidth={2} className="text-nx-lime" aria-hidden="true" />}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
